"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslations } from "next-intl";

import GlowButton from "../../ui/GlowButton";

// "If you," stays fixed; the question/answer pair behind it swaps vertically.
// Keys map into the `home.hero.ifYou` message namespace; the count is stable.
//
// Keep the desktop question on one line inside the fixed-height swap window.
// Use a smaller headline at xl so the answer and CTA retain enough space.
const ITEM_KEYS = ["item1", "item2"];
const SWAP_INTERVAL = 2500;

function Pair({ item }) {
  return (
    <>
      <p className="text-2xl font-bold text-white lg:shrink-0 lg:whitespace-nowrap lg:text-[26px] lg:leading-[40px] xl:text-[32px] xl:leading-[50px]">
        {item.bold}
      </p>
      <p className="text-lg text-white lg:ml-3 lg:text-base lg:leading-[24px] xl:text-xl xl:leading-[30px]">
        {item.normal}
      </p>
    </>
  );
}

export default function IfYouCard() {
  const t = useTranslations("home.hero");
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);

  const items = ITEM_KEYS.map((key) => ({
    bold: t(`ifYou.${key}.bold`),
    normal: t(`ifYou.${key}.normal`),
  }));

  // Auto-rotate on a timer; paused entirely when the user prefers reduced motion.
  useEffect(() => {
    if (reduce) return undefined;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % ITEM_KEYS.length),
      SWAP_INTERVAL,
    );
    return () => clearInterval(id);
  }, [reduce]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="relative w-full overflow-hidden rounded-[16px] border border-white/20 bg-black/70 p-6 backdrop-blur-[10px] shadow-[inset_0px_4px_4px_0px_rgba(255,255,255,0.1)] lg:px-10 lg:py-6 xl:px-12 xl:py-7"
    >
      <div className="flex flex-col gap-3 lg:h-[40px] lg:flex-row lg:items-center lg:gap-3 xl:h-[50px] xl:gap-4">
        <span className="shrink-0 text-3xl font-bold text-white lg:text-[28px] lg:leading-[40px] xl:text-[40px] xl:leading-[50px]">
          {t("ifYou.prefix")}
        </span>

        {/* Both variants are always rendered and chosen with a CSS media
            variant. Branching the DOM on `useReducedMotion()` instead would
            make the server and client render different trees, and the
            resulting hydration mismatch tears down the whole hero's markup. */}

        {/* Reduced motion: show both pairs at once, no animation. */}
        <div className="hidden flex-1 flex-col gap-4 motion-reduce:flex">
          {items.map((item) => (
            <div
              key={item.bold}
              className="flex flex-col gap-1 lg:flex-row lg:items-center lg:justify-between"
            >
              <Pair item={item} />
            </div>
          ))}
        </div>

        {/* Vertical swap: outgoing pair slides up, incoming rises from below. */}
        <div
          aria-live="polite"
          className="relative h-[112px] w-full overflow-hidden motion-reduce:hidden lg:h-[42px] lg:flex-1 xl:h-[52px]"
        >
          <AnimatePresence initial={false}>
            <motion.div
              key={index}
              initial={{ y: "110%", opacity: 0 }}
              animate={{ y: "0%", opacity: 1 }}
              exit={{ y: "-110%", opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="absolute inset-0 flex flex-col justify-center gap-1 lg:flex-row lg:items-center lg:justify-between"
            >
              <Pair item={items[index]} />
            </motion.div>
          </AnimatePresence>
        </div>

        <GlowButton href="/contact-us" className="mt-3 self-start lg:mt-0 lg:ml-3 lg:self-center xl:ml-4">
          {t("ifYou.cta")}
        </GlowButton>
      </div>
    </motion.div>
  );
}
